import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Scopes the listener to the nearest enclosing product context. `closest` returns the
 * nearest ancestor matching any of these, so a card/dialog always wins over the section
 * it sits in and a selection on one product never rewrites another's SKU. The section
 * fallback covers instances outside a product-information block (e.g. the Subscribe &
 * Save widget rendered by the mini-buybox).
 */
const SKU_SCOPE_SELECTOR =
  '[id*="ProductInformation-"], [id*="QuickAdd-"], product-card, custom-product-card, quick-add-dialog, dialog, .shopify-section';

/**
 * A custom element that displays a product SKU.
 * This component listens for variant update events and updates the SKU display accordingly.
 * It handles SKU updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer - The container element for the SKU
 * @property {HTMLElement} sku - The span element that displays the SKU text
 *
 * @extends {Component<Refs>}
 */
class ProductSkuComponent extends Component {
  requiredRefs = ['skuContainer', 'sku'];

  /** @type {Element | null} */
  #scope = null;

  connectedCallback() {
    super.connectedCallback();
    this.#scope = this.closest(SKU_SCOPE_SELECTOR);
    this.#scope?.addEventListener(ThemeEvents.variantUpdate, this.updateSku);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Uses the stored scope: `closest` no longer resolves once the element is detached.
    this.#scope?.removeEventListener(ThemeEvents.variantUpdate, this.updateSku);
    this.#scope = null;
  }

  /**
   * Updates the SKU.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateSku = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Use the variant data from the event
    // The variant is in event.detail.resource
    if (event.detail.resource) {
      const variantSku = event.detail.resource.sku || '';

      if (variantSku) {
        // Show the component and update the SKU
        this.style.display = 'block';
        this.refs.sku.textContent = variantSku;
      } else {
        // Hide the entire component when SKU is empty
        this.style.display = 'none';
        this.refs.sku.textContent = '';
      }
    }
  };
}

if (!customElements.get('product-sku-component')) {
  customElements.define('product-sku-component', ProductSkuComponent);
}
